import React, { useEffect, useState } from "react";
import Breadcrumb from "../../components/Breadcrumb";
import { GET_COMPANY_USERS } from "../../graphql/companyQueries";
import { useQuery, useMutation } from "@apollo/client";
import { useParams } from "react-router-dom";
import Toast from "../../components/Toast/Toast";
import {
  EmptyState,
  ErrorState,
  skeletonTableProps,
} from "../../components/states";
import {
  Button,
  Dropdown,
  Modal,
  Input,
  Segmented,
  Space,
  Table,
  Tooltip,
  Checkbox,
} from "antd";

import {
  ImportOutlined,
  FileOutlined,
  DeleteOutlined,
  PlusSquareOutlined,
  CheckOutlined,
  CloseOutlined,
  FilterFilled,
  MoreOutlined,
} from "@ant-design/icons";
import { formatPhoneNumber } from "../../utils/phoneFormat";
import {
  EditOutlined as EditIcon,
  PlusOutlined as PlusIcon,
  ReloadOutlined as ReloadIcon,
} from "@ant-design/icons";
import UserFormNew from "../../components/users/UserFormNew";
import {
  DELETE_USER,
  REACTIVE_USER,
  UPDATE_USER_PASSWORD,
  UPDATE_USER,
} from "../../graphql/userQueries";
import { useToast } from "../../components/Toast/toastHook";
import BulkUserUpload from "./BulkUserUpload";
import { BsFiletypeCsv } from "react-icons/bs";
import ToggleButton from "../../components/ToggleButton";
import useConfirmDialog from "../../components/ConfirmDialog/useConfirmDialog";
import TableToolbar from "../../components/TableToolbar/TableToolbar";
import { compareDate, compareText, sortByColumn } from "../../utils/tableSort";
import { TABLE_SCROLL } from "../../constants/table";
import useIsPhone from "../../hooks/useIsPhone";
import CardList from "../../components/CardList/CardList";
import UserCard from "../../components/users/UserCard";

const INITIAL_SELECTED_COLUMNS = {
  firstName: false,
  fullName: false,
  phone: false,
  role: false,
  defaultOffice: false,
  lastName: false,
  email: false,
  lastLogin: false,
  createdAt: false,
};

const COLUMN_MAPPINGS = {
  firstName: "First Name",
  fullName: "Full Name",
  phone: "Phone",
  role: "Role",
  defaultOffice: "Office",
  lastName: "Last Name",
  email: "Email",
  lastLogin: "Last Login",
  createdAt: "Account Created",
};

// Two separate flags: Status is the isActive toggle; Activated Accounts is
// isPasswordReseted (the user replaced the onboarding password with their own).
const DEFAULT_STATUS = "active";
const STATUS_FILTERS = [
  { text: "Active", value: "active" },
  { text: "Inactive", value: "inactive" },
];
const statusOf = (user) => (user.isActive ? "active" : "inactive");

const ACTIVATION_FILTERS = [
  { text: "Activated", value: "activated" },
  { text: "Not activated", value: "notActivated" },
];
const activationOf = (user) =>
  user.isPasswordReseted ? "activated" : "notActivated";

// Header filter that applies on click — All plus the column's options, no
// Reset/OK step — in the brand color instead of antd's default blue. It only
// sets page state: the page filters `visibleUsers` itself, so the count and
// the phone cards follow the same filters.
// `filteredValue` must be controlled: uncontrolled, antd reads filter state once
// at mount — from the skeleton columns — and the icon never highlights.
const columnFilter = (options, value, onSelect) => ({
  filteredValue: value ? [value] : null,
  filterIcon: (filtered) => (
    <FilterFilled className={filtered ? "text-primary" : undefined} />
  ),
  filterDropdown: ({ close }) => (
    <ul role="menu" className="m-0 min-w-[150px] list-none p-1">
      {[{ text: "All", value: null }, ...options].map((option) => {
        const selected = option.value === value;
        return (
          <li key={option.text} role="none">
            <button
              type="button"
              role="menuitemradio"
              aria-checked={selected}
              onClick={() => {
                onSelect(option.value);
                close();
              }}
              className={`flex w-full items-center justify-between gap-3 rounded-md px-3 py-1.5 text-left text-sm hover:bg-gray-100 ${
                selected ? "font-medium text-primary" : ""
              }`}
            >
              {option.text}
              {selected && <CheckOutlined aria-hidden="true" />}
            </button>
          </li>
        );
      })}
    </ul>
  ),
});

// Phones have no column headers, so the same filters sit in the toolbar.
const ALL = "all";
const phoneOptions = (filters) => [
  { label: "All", value: ALL },
  ...filters.map(({ text, value }) => ({ label: text, value })),
];

// One comparator per sortable column.
const SORTERS = {
  fullName: (a, b) => compareText(a.fullName, b.fullName),
  roleName: (a, b) => compareText(a.role?.name, b.role?.name),
  defaultOffice: (a, b) => compareText(a.defaultOffice?.name, b.defaultOffice?.name),
  email: (a, b) => compareText(a.email, b.email),
  createdAt: (a, b) => compareDate(a.createdAt, b.createdAt),
  lastLogin: (a, b) => compareDate(a.lastLogin, b.lastLogin),
};

const UsersNew = (props) => {
  const [open, setOpen] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [users, setUsers] = useState(null);
  // null = no filter on that column.
  const [statusFilter, setStatusFilter] = useState(DEFAULT_STATUS);
  const [activationFilter, setActivationFilter] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [resetAgent, setResetAgent] = useState(null);
  const [currentUserPassword, setCurrentUserPassword] = useState(null);
  const [isAddEditForm, setIsAddEditForm] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [selectedColumns, setSelectedColumns] = useState(
    INITIAL_SELECTED_COLUMNS
  );

  const toast = useToast(4000);
  const { confirm, dialog } = useConfirmDialog();
  const isPhone = useIsPhone();

  const { companyId } = useParams();

  const showModal = () => {
    setOpen(true);
  };

  const handleOk = () => {
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleCancelExportModal = () => {
    setExportModal(false);
    setSelectedColumns(INITIAL_SELECTED_COLUMNS);
  };

  const handledSelectedColumnsChange = (event) => {
    setSelectedColumns((prev) => ({
      ...prev,
      [event.target.name]: event.target.checked,
    }));
  };

  const { loading, error, data, refetch } = useQuery(GET_COMPANY_USERS, {
    fetchPolicy: "no-cache",
    variables: { companyId },
    onError(err) {
      Toast(
        "error",
        "There was an error loading the users. If the issue persists please contact the administrators"
      );
    },
  });

  // Sorting is the only per-column control; search lives in the toolbar.
  // `sortOrder` is controlled so Reset can clear it.
  const sortableColumn = (key) => ({
    sorter: SORTERS[key],
    sortOrder: sortedInfo.columnKey === key ? sortedInfo.order : null,
  });

  const isExportActive = (min = 3) => {
    let selectedCount = 0;

    for (const column in selectedColumns) {
      if (!selectedColumns[column]) continue;
      selectedCount += 1;
    }

    return !(selectedCount >= min);
  };

  useEffect(() => {
    if (data) setUsers(data.getCompanyUsers);

    if (error) toast("error", error.message);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error]);

  const [deleteCompanyUser] = useMutation(DELETE_USER, {
    // refetchQueries: [
    //   {
    //     query: GET_COMPANY_USERS,
    //     variables: {
    //       companyId,
    //     },
    //   },
    // ],
    onCompleted(result) {
      toast(
        "success",
        `You have successfully deleted ${result.deleteCompanyUser.firstName}`
      );
      refetch({ companyId });
    },
  });
  const [reactivateCompanyUser] = useMutation(REACTIVE_USER, {
    // refetchQueries: [
    //   {
    //     query: GET_COMPANY_USERS,
    //     variables: {
    //       companyId,
    //     },
    //   },
    // ],
    onCompleted(result) {
      toast(
        "success",
        `You have successfully reactivated ${result.reActiveUser.firstName}`
      );
      refetch({ companyId });
    },
  });
  const [updateCompanyUser] = useMutation(UPDATE_USER, {
    onCompleted(result) {
      console.log("resss", result);
      toast(
        "success",
        `${result?.updateUser?.firstName}  ${
          result?.updateUser?.isActive ? "Activated" : "Inactivated"
        } successfully.`
      );
      refetch({ companyId });
    },
  });

  const handleDelete = (userId) =>
    deleteCompanyUser({ variables: { userId } });

  const reactivateUser = (userId) =>
    reactivateCompanyUser({ variables: { userId, companyId } });

  const handleSatusChange = (userId, status) => {
    updateCompanyUser({
      variables: {
        userInput: {
          _id: userId,
          isActive: status,
        },
      },
    });
  };

  const getAccessType = (role) => {
    if (role === "ADMIN") return "DASHBOARD";
    if (role === "ADMIN 1") return "DASHBOARD & APP";
    if (role === "AGENT") return "APP";
    if (role === "BUYER_AGENT") return "APP";
    return "UNKNOWN";
  };

  // Row actions, shared by the table's Action column and the phone cards.
  const editUser = (record) => {
    setSelectedAgent(record);
    setIsAddEditForm(true);
  };

  const askResetPassword = (record) =>
    confirm({
      title: `Reset password for ${record.fullName}?`,
      description: "You will choose the new password on the next step.",
      confirmLabel: "Continue",
      danger: false,
      icon: <ReloadIcon />,
      onConfirm: () => {
        setResetAgent(record);
        showModal();
      },
    });

  const askDelete = (record) =>
    confirm({
      title: `Delete ${record.fullName}?`,
      description:
        "This user will lose access to Fyrstup. You can reactivate them later from this list.",
      onConfirm: () => handleDelete(record.id),
    });

  const askReactivate = (record) =>
    confirm({
      title: `Reactivate ${record.fullName}?`,
      description: "They will get access to Fyrstup again.",
      confirmLabel: "Reactivate",
      danger: false,
      icon: <PlusSquareOutlined />,
      onConfirm: () => reactivateUser(record.id),
    });

  const statusToggle = ({ isActive, id }) => (
    <ToggleButton status={isActive} statusHandler={handleSatusChange} id={id} />
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "fullName",
      key: "fullName",
      render: (text) => <p>{text}</p>,
      ...sortableColumn("fullName"),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      className: "whitespace-nowrap",
      render: (text) => <p>{formatPhoneNumber(text)}</p>,
    },
    {
      title: "Role",
      key: "roleName",
      dataIndex: "role",
      render: (_, { role }) => <p>{role.name.toUpperCase()}</p>,
      ...sortableColumn("roleName"),
    },
    {
      title: "Access Type",
      key: "role",
      dataIndex: "role",
      render: (_, { role }) => <p>{getAccessType(role.name.toUpperCase())}</p>,
    },
    {
      title: "Office",
      key: "defaultOffice",
      dataIndex: "defaultOffice",
      render: (_, { defaultOffice }) => (
        <p>{defaultOffice ? defaultOffice.name : "N/A"}</p>
      ),
      ...sortableColumn("defaultOffice"),
    },
    {
      dataIndex: "email",
      key: "email",
      title: "Email",
      ...sortableColumn("email"),
    },
    {
      title: "Activated Accounts",
      key: "activated",
      dataIndex: "activated",
      ...columnFilter(ACTIVATION_FILTERS, activationFilter, setActivationFilter),
      render: (_, { lastLogin, isPasswordReseted }) => {
        return (
          <div className="w-full flex justify-center">
            {isPasswordReseted ? (
              <CheckOutlined style={{ fontSize: "22px", color: "green" }} />
            ) : (
              <CloseOutlined style={{ fontSize: "22px", color: "red" }} />
            )}
          </div>
        );
      },
    },
    {
      title: "Account Created",
      key: "createdAt",
      dataIndex: "createdAt",
      render: (_, { createdAt }) => {
        const userCreatedAt = new Date(parseInt(createdAt, 10));

        const formatted = userCreatedAt.toLocaleDateString("en-US", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });

        return <p>{formatted}</p>;
      },
      ...sortableColumn("createdAt"),
    },
    {
      title: "Last Login",
      key: "lastLogin",
      dataIndex: "lastLogin",
      render: (_, { lastLogin }) => {
        const userCreatedAt = new Date(
          parseInt(lastLogin ? lastLogin : null, 10)
        );

        const formatted = userCreatedAt.toLocaleDateString("en-US", {
          year: "2-digit",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        });

        return <p>{formatted !== "Invalid Date" ? formatted : "--"}</p>;
      },
      ...sortableColumn("lastLogin"),
    },
    {
      title: "Status",
      key: "isActive",
      dataIndex: "isActive",
      ...columnFilter(STATUS_FILTERS, statusFilter, setStatusFilter),
      render: (_, record) => statusToggle(record),
    },
    {
      title: "Action",
      key: "x",
      render: (_, record) =>
        !record?.deletedAt ? (
          <div className="table-action-btns">
            <Button
              className="border-primary text-primary"
              icon={<EditIcon />}
              onClick={() => editUser(record)}
            />
            <Button
              className="border-secondary text-secondary"
              icon={<ReloadIcon />}
              onClick={() => askResetPassword(record)}
            />

            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => askDelete(record)}
            />
          </div>
        ) : (
          <div>
            <Tooltip title="Reactivate">
              <Button
                danger
                icon={<PlusSquareOutlined />}
                onClick={() => askReactivate(record)}
              />
            </Tooltip>
          </div>
        ),
    },
  ];

  function onChange(pagination, filters, sorter) {
    setSortedInfo(sorter);
  }

  // Reset: back to how the page opens (Active only).
  const clearAll = () => {
    setSortedInfo({});
    setStatusFilter(DEFAULT_STATUS);
    setActivationFilter(null);
    setGlobalSearch("");
  };

  // Clear filters: show everyone — the default Active filter can be what hides them.
  const showAllUsers = () => {
    setStatusFilter(null);
    setActivationFilter(null);
    setGlobalSearch("");
  };

  const firstLoad = loading && !users?.length;

  // One search box across the columns an admin actually scans for.
  const needle = globalSearch.trim().toLowerCase();
  const visibleUsers = (users ?? []).filter(
    (u) =>
      (!statusFilter || statusOf(u) === statusFilter) &&
      (!activationFilter || activationOf(u) === activationFilter) &&
      (!needle ||
        [u.fullName, u.email, u.phone, u.role?.name, u.defaultOffice?.name]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(needle)))
  );
  const hasActiveControls = Boolean(
    needle ||
      statusFilter !== DEFAULT_STATUS ||
      activationFilter ||
      sortedInfo.order
  );

  const emptyState = users?.length ? (
    <EmptyState
      title="No users match these filters"
      description="Try a different search or status filter."
      actionLabel="Clear filters"
      onAction={showAllUsers}
    />
  ) : (
    <EmptyState
      title="No users yet"
      description="Users you add to this company will appear here."
    />
  );

  // Phones fold the import/export buttons above the toolbar into a menu.
  const moreActions = {
    items: [
      { key: "import", icon: <ImportOutlined />, label: "Bulk Agents Import" },
      { key: "export", icon: <FileOutlined />, label: "Export Active Agents" },
      { key: "csv", icon: <BsFiletypeCsv />, label: "Export all columns (CSV)" },
    ],
    onClick: ({ key }) => {
      if (key === "import") {
        setSelectedAgent(null);
        setShowBulkImport(true);
      } else if (key === "export") {
        setExportModal(true);
      } else {
        handleExportAllColumns();
      }
    },
  };

  const listView = () => (
    <div>
      <Breadcrumb pageName="Users" />
      {error && !users?.length ? (
        <ErrorState onRetry={() => refetch()} />
      ) : (
        <div className="py-6">
          {!isPhone && (
            <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
              <div>
                <Space wrap>
                  <Button
                    // onClick={() => navigate(`/company/${companyId}/users-upload`)}
                    onClick={() => {
                      setSelectedAgent(null);
                      setShowBulkImport(true);
                    }}
                    icon={<ImportOutlined />}
                  >
                    Bulk Agents Import
                  </Button>
                  <Button
                    icon={<FileOutlined />}
                    onClick={() => setExportModal(true)}
                  >
                    Export Active Agents
                  </Button>
                </Space>
              </div>
              <BsFiletypeCsv
                size={40}
                className="hover:cursor-pointer hover:text-blue"
                onClick={handleExportAllColumns}
              />
            </div>
          )}

          <TableToolbar
            title="Users"
            count={visibleUsers.length}
            searchValue={globalSearch}
            onSearch={setGlobalSearch}
            searchPlaceholder="Search name, email, phone, office..."
            filters={
              <>
                {isPhone && (
                  <>
                    <Segmented
                      aria-label="Filter by status"
                      options={phoneOptions(STATUS_FILTERS)}
                      value={statusFilter ?? ALL}
                      onChange={(value) =>
                        setStatusFilter(value === ALL ? null : value)
                      }
                    />
                    <Segmented
                      aria-label="Filter by account activation"
                      options={phoneOptions(ACTIVATION_FILTERS)}
                      value={activationFilter ?? ALL}
                      onChange={(value) =>
                        setActivationFilter(value === ALL ? null : value)
                      }
                    />
                  </>
                )}
                {hasActiveControls && (
                  <Button type="link" onClick={clearAll}>
                    Reset
                  </Button>
                )}
              </>
            }
            action={
              <Space>
                <Button
                  className="border-primary text-primary"
                  icon={<PlusIcon />}
                  onClick={() => editUser(null)}
                >
                  Add New
                </Button>
                {isPhone && (
                  <Dropdown menu={moreActions} trigger={["click"]} placement="bottomRight">
                    <Button aria-label="More actions" icon={<MoreOutlined />} />
                  </Dropdown>
                )}
              </Space>
            }
          />
          {isPhone ? (
            <CardList
              label="Users"
              loading={firstLoad}
              items={sortByColumn(visibleUsers, columns, sortedInfo)}
              resetKey={`${needle}|${statusFilter}|${activationFilter}`}
              columns={columns}
              sort={sortedInfo}
              onSortChange={setSortedInfo}
              rowKey={(record) => record.id}
              empty={emptyState}
              renderItem={(record) => (
                <UserCard
                  user={record}
                  accessType={getAccessType(record.role?.name?.toUpperCase())}
                  statusToggle={statusToggle(record)}
                  onEdit={() => editUser(record)}
                  onResetPassword={() => askResetPassword(record)}
                  onDelete={() => askDelete(record)}
                  onReactivate={() => askReactivate(record)}
                />
              )}
            />
          ) : (
            <Table
              scroll={TABLE_SCROLL}
              bordered
              rowKey={(record) => record?.id}
              onChange={onChange}
              {...(firstLoad
                ? skeletonTableProps(columns)
                : {
                    columns,
                    dataSource: visibleUsers,
                    pagination: { position: ["bottomCenter"] },
                  })}
              locale={{ emptyText: emptyState }}
            />
          )}
        </div>
      )}
    </div>
  );

  const handleOnUserFormCancel = () => {
    setSelectedAgent(null);
    setIsAddEditForm(false);
  };

  const userBulkImport = () => (
    <div>
      <BulkUserUpload
        refetchUsers={refetch}
        onCancel={() => {
          setShowBulkImport(false);
        }}
      />
    </div>
  );

  const userForm = () => (
    <UserFormNew
      users={users}
      companyId={companyId}
      selectedUser={selectedAgent}
      handleOnCancel={handleOnUserFormCancel}
      handleOnComplete={handleOnUserFormCancel}
      refetchUsers={refetch}
    />
  );

  const handleExportSelectedColumns = () => {
    const checkedColumns = Object.keys(selectedColumns).filter(
      (col) => selectedColumns[col]
    );

    exportAgents(checkedColumns);
  };

  const handleExportAllColumns = () => {
    exportAgents(Object.keys(COLUMN_MAPPINGS));
  };

  const exportAgents = (checkedColumns) => {
    try {
      const userCSVData = [];

      const activeUsers = (data?.getCompanyUsers ?? []).filter(
        (user) => !user?.deletedAt
      );

      if (!activeUsers.length) {
        toast("error", "No agents to export yet.");
        return;
      }

      activeUsers.forEach((user) => {
        const columnsData = {};

        for (const column of checkedColumns) {
          if (column === "role") {
            columnsData[COLUMN_MAPPINGS[column]] =
              user[column].name.toUpperCase();
            continue;
          }

          if (column === "defaultOffice") {
            columnsData[COLUMN_MAPPINGS[column]] = user[column].name ?? "";
            continue;
          }

          if (column === "lastLogin" || column === "createdAt") {
            const userCreatedAt = new Date(parseInt(user[column], 10));

            const formatted = userCreatedAt.toLocaleDateString("en-US", {
              year: "2-digit",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            });

            columnsData[COLUMN_MAPPINGS[column]] =
              formatted === "Invalid Date" ? "" : formatted;
            continue;
          }

          columnsData[COLUMN_MAPPINGS[column]] = user[column] ?? "";
        }

        userCSVData.push(columnsData);
      });

      const csvData = userCSVData.map((user) =>
        Object.values(user)
          .map((value) => JSON.stringify(value))
          .join(",")
      );

      const csvHeaders = Object.keys(userCSVData[0]);
      csvData.unshift(csvHeaders.join(","));

      const csvContent = csvData.join("\n");

      const blob = new Blob([csvContent], { type: "text/csv" });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = "user_data.csv";

      a.click();

      window.URL.revokeObjectURL(url);

      toast("success", "Active agents exported successfully.");
    } catch (error) {
      console.log("error in handleExportAgents", error.message);
      toast("error", "There was an error in exporting active agents.");
    }
  };

  const [updatePassword] = useMutation(UPDATE_USER_PASSWORD, {
    async onCompleted(result) {
      toast(
        "success",
        `Successfully updated password for ${resetAgent.fullName}!`
      );
      setResetAgent(null);
      setCurrentUserPassword(null);
    },
    onError(e) {
      console.error(e);
      toast(
        "error",
        "An issue occurred! Please check your credentials and try again"
      );
    },
  });

  return (
    <div>
      {!isAddEditForm
        ? !showBulkImport
          ? listView()
          : userBulkImport()
        : userForm()}
      {dialog}
      <Modal
        open={open}
        title={`Reset Password for ${resetAgent?.fullName}`}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={(_, { OkBtn, CancelBtn }) => (
          <>
            <Button
              type="primary"
              className="border-primary text-primary"
              loading={loading}
              onClick={() => {
                handleOk();
                updatePassword({
                  variables: {
                    userId: resetAgent.id,
                    password: currentUserPassword,
                  },
                });
              }}
            >
              Save
            </Button>
            {/* <Button className="border-red text-red">Reset to Onboarding Password</Button> */}
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      >
        <Input
          value={currentUserPassword}
          placeholder="Enter New password"
          onChange={(event) => {
            setCurrentUserPassword(event.target.value);
          }}
        />
      </Modal>
      <Modal
        open={exportModal}
        title="Export Active Agents"
        onOk={handleCancelExportModal}
        onCancel={handleCancelExportModal}
        footer={() => (
          <div className="mt-3 flex">
            <Button
              type="primary"
              className="border-primary text-primary"
              loading={loading}
              onClick={() => {
                handleExportSelectedColumns();
                handleCancelExportModal();
              }}
              disabled={isExportActive()}
            >
              Export
            </Button>
            <Button key="back" onClick={handleCancelExportModal}>
              Cancel
            </Button>
          </div>
        )}
      >
        <div className="mt-4">
          <p>
            Please select 3 or more of the following fields you want to see
            exported:
          </p>
          <div className="grid grid-cols-2 mt-5">
            <div className="flex flex-col">
              <Checkbox
                name="firstName"
                checked={selectedColumns.firstName}
                onChange={handledSelectedColumnsChange}
              >
                First Name
              </Checkbox>
              <Checkbox
                name="fullName"
                checked={selectedColumns.fullName}
                onChange={handledSelectedColumnsChange}
              >
                Full Name
              </Checkbox>
              <Checkbox
                name="phone"
                checked={selectedColumns.phone}
                onChange={handledSelectedColumnsChange}
              >
                Phone
              </Checkbox>
              <Checkbox
                name="role"
                checked={selectedColumns.role}
                onChange={handledSelectedColumnsChange}
              >
                Role
              </Checkbox>
              <Checkbox
                name="defaultOffice"
                checked={selectedColumns.defaultOffice}
                onChange={handledSelectedColumnsChange}
              >
                Office
              </Checkbox>
            </div>
            <div className="flex flex-col">
              <Checkbox
                name="lastName"
                checked={selectedColumns.lastName}
                onChange={handledSelectedColumnsChange}
              >
                Last Name
              </Checkbox>
              <Checkbox
                name="email"
                checked={selectedColumns.email}
                onChange={handledSelectedColumnsChange}
              >
                Email
              </Checkbox>
              <Checkbox
                name="createdAt"
                checked={selectedColumns.createdAt}
                onChange={handledSelectedColumnsChange}
              >
                Account Created
              </Checkbox>
              <Checkbox
                name="lastLogin"
                checked={selectedColumns.lastLogin}
                onChange={handledSelectedColumnsChange}
              >
                Last Login
              </Checkbox>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default UsersNew;
